import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, List, Heading2, Heading3 } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 p-2 border-b border-stone-200 bg-stone-50">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive('heading', { level: 2 }) ? 'bg-burgundy/10 text-burgundy' : 'text-charcoal-600'
        )}
        title="Überschrift 2"
        data-testid="menu-editor-h2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive('heading', { level: 3 }) ? 'bg-burgundy/10 text-burgundy' : 'text-charcoal-600'
        )}
        title="Überschrift 3"
        data-testid="menu-editor-h3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-stone-300 mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive('bold') ? 'bg-burgundy/10 text-burgundy' : 'text-charcoal-600'
        )}
        title="Fett (Strg+B)"
        data-testid="menu-editor-bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive('italic') ? 'bg-burgundy/10 text-burgundy' : 'text-charcoal-600'
        )}
        title="Kursiv (Strg+I)"
        data-testid="menu-editor-italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-stone-300 mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive('bulletList') ? 'bg-burgundy/10 text-burgundy' : 'text-charcoal-600'
        )}
        title="Aufzählungsliste"
        data-testid="menu-editor-list"
      >
        <List className="h-4 w-4" />
      </Button>
    </div>
  );
};

const MenuEditor = ({ value, onChange, placeholder = "Menü eingeben..." }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: placeholder,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Convert empty editor to empty string
      if (html === '<p></p>') {
        onChange('');
      } else {
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3 text-charcoal-800',
      },
    },
  });

  // Update editor content when value changes externally
  React.useEffect(() => {
    if (editor && value !== editor.getHTML() && value !== undefined) {
      // Only update if the content is actually different
      const currentContent = editor.getHTML();
      if (currentContent === '<p></p>' && !value) return;
      if (value !== currentContent) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  return (
    <div className="border border-stone-300 rounded-md overflow-hidden bg-white" data-testid="menu-editor">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #722F37;
          margin: 1rem 0 0.5rem 0;
          border-bottom: 1px solid #E5E5E5;
          padding-bottom: 0.25rem;
        }
        .ProseMirror h2:first-child {
          margin-top: 0;
        }
        .ProseMirror h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #722F37;
          margin: 0.75rem 0 0.25rem 0;
        }
        .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror li {
          margin: 0.25rem 0;
        }
        .ProseMirror strong {
          font-weight: 700;
          color: #722F37;
        }
        .ProseMirror em {
          font-style: italic;
        }
        .ProseMirror p {
          margin: 0.25rem 0;
        }
      `}</style>
    </div>
  );
};

export default MenuEditor;
